import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import { isAdminUser } from '@/config/admin';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Process Google auth callback
  const handleHashParams = async () => {
    // Check if we're returning from a Google auth redirect
    const isGoogleAuthInProgress = localStorage.getItem('google_auth_in_progress');
    
    if (isGoogleAuthInProgress) {
      console.log('Detected Google auth in progress, processing callback');
      
      // Get hash parameters from URL if present
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        console.log('Access token found in URL hash, processing auth state');
        
        try {
          // Force a session refresh to process the hash
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Error getting session after redirect:', error);
            toast.error('Authentication failed. Please try again.');
          } else if (data.session) {
            console.log('Session established!', data.session.user.id);
            // toast.success('Signed in successfully!');
            
            // Navigate to dashboard
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Error processing auth callback:', error);
        } finally {
          // Clear Google auth flag
          localStorage.removeItem('google_auth_in_progress');
        }
      }
    }
  };

  useEffect(() => {
    // Check for existing session on initial load
    const initAuth = async () => {
      setIsLoading(true);
      try {
        // Check for auth callback
        await handleHashParams();
        
        // Get the current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log('Found existing session:', session.user.id);
          setSession(session);
          setUser(session.user);
          
          // Set flag to prevent duplicate redirects
          if (!localStorage.getItem('hasSignedInOnce')) {
            localStorage.setItem('hasSignedInOnce', 'true');
            navigate('/dashboard');
          }
        }
      } catch (error) {
        console.error('Error checking auth session:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    initAuth();
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('Auth state changed:', event, newSession?.user?.id);
        
        setSession(newSession);
        setUser(newSession?.user ?? null);
  
        if (event === 'SIGNED_IN') {
          console.log('User signed in:', newSession?.user?.id);
          const hasSignedInBefore = localStorage.getItem('hasSignedInOnce');
  
          if (!hasSignedInBefore) {
            localStorage.setItem('hasSignedInOnce', 'true');
            // toast.success('Signed in successfully');
            navigate('/dashboard');
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          localStorage.removeItem('hasSignedInOnce');
          localStorage.removeItem('google_auth_in_progress');
          // toast.info('Signed out');
          navigate('/');
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('Token refreshed');
        } else if (event === 'USER_UPDATED') {
          console.log('User updated');
        }
      }
    );
  
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);
  
  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      // Check if user is admin (bypass waitlist for admins)
      const isAdmin = isAdminUser(email);
      
      if (!isAdmin) {
        // Only check waitlist status for non-admin users
        const { data: waitlistStatus, error: statusError } = await supabase
          .rpc('get_waitlist_status', { user_email: email });
        
        if (statusError) {
          console.error('Error checking waitlist status:', statusError);
          toast.error('Failed to check waitlist status: ' + statusError.message);
          return;
        }
        
        // If user is not approved, show specific message
        if (waitlistStatus === 'pending') {
          toast.error('Your account is still pending approval. Please wait for admin approval.');
          return;
        } else if (waitlistStatus === 'rejected') {
          toast.error('Your waitlist application was not approved.');
          return;
        } else if (waitlistStatus === 'not_found') {
          toast.error('Please join the waitlist first before attempting to sign in.');
          return;
        }
      }
      
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) throw error;
    } catch (error: any) {
      // More specific error messages
      if (error.message?.includes('fetch')) {
        toast.error('Network connection failed. Please check your internet connection and try again.');
      } else if (error.message?.includes('Invalid login credentials')) {
        toast.error('Invalid email or password. Please try again.');
      } else {
        toast.error(error.message || 'Error signing in');
      }
      console.error('Error signing in:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      // Check if user is admin (bypass waitlist for admins)
      const isAdminUserFlag = isAdminUser(email);
      
      if (!isAdminUserFlag) {
        // Only check waitlist status for non-admin users
        const { data: waitlistStatus, error: statusError } = await supabase
          .rpc('get_waitlist_status', { user_email: email });
        
        if (statusError) {
          console.error('Error checking waitlist status:', statusError);
        }
        
        // Block signup if not approved
        if (waitlistStatus !== 'approved') {
          if (waitlistStatus === 'pending') {
            toast.error('Your account is pending approval. Please wait for admin approval before setting up your account.');
          } else if (waitlistStatus === 'rejected') {
            toast.error('Your waitlist application was not approved.');
          } else {
            toast.error('Please join the waitlist first. Only approved users can create accounts.');
          }
          return;
        }
      } else {
        console.log('Admin user detected, bypassing waitlist check for signup');
      }
      
      const { error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: window.location.origin + '/auth'
        }
      });
      
      if (error) throw error;
      
      toast.success('Registration successful! Check your email for confirmation.');
    } catch (error: any) {
      toast.error(error.message || 'Error signing up');
      console.error('Error signing up:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({ 
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth',
          queryParams: {
            prompt: 'select_account',
            access_type: 'offline'
          }
        }
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message || 'Error signing in with Google');
      console.error('Error signing in with Google:', error);
      setIsLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear any auth-related items from localStorage
      localStorage.removeItem('hasSignedInOnce');
      localStorage.removeItem('google_auth_in_progress');
      localStorage.removeItem('auth_attempt_id');
    } catch (error: any) {
      toast.error(error.message || 'Error signing out');
      console.error('Error signing out:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, signIn, signUp, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
